/**
 * Silero VAD Service
 *
 * Wraps @ricky0123/vad-web (Silero VAD v5) for voice activity detection.
 * Used in open-mic mode to automatically detect when the user starts and
 * stops speaking — without requiring a push-to-talk button press.
 *
 * Key design decisions:
 *   - Dynamic import of @ricky0123/vad-web to avoid loading ONNX runtime
 *     unless open-mic mode is actually activated
 *   - Accepts an existing MediaStream to share the mic with AudioCaptureService
 *   - Custom pauseStream/resumeStream handlers to avoid stopping shared tracks
 *   - Uses Silero V5 model for improved accuracy
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VADServiceCallbacks {
  /** Fired when speech is initially detected (may be a misfire) */
  onSpeechStart: () => void
  /** Fired when speech is confirmed and then ends (includes full audio buffer) */
  onSpeechEnd: (audio: Float32Array) => void
  /** Fired when initial speech detection was too short (false positive) */
  onVADMisfire: () => void
}

export interface VADServiceConfig {
  /** Probability threshold to detect speech start (0-1). Default: 0.3 */
  positiveSpeechThreshold?: number
  /** Probability below which speech is considered ended (0-1). Default: 0.25 */
  negativeSpeechThreshold?: number
  /** Grace period after speech end detected (ms). Default: 1400 */
  redemptionMs?: number
  /** Minimum speech duration to trigger onSpeechEnd (ms). Default: 400 */
  minSpeechMs?: number
  /** Audio to prepend before speech start (ms). Default: 800 */
  preSpeechPadMs?: number
}

// Use a type alias for the MicVAD instance since we dynamically import
type MicVADInstance = {
  start: () => Promise<void>
  pause: () => Promise<void>
  destroy: () => Promise<void>
  listening: boolean
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class SileroVADService {
  private micVAD: MicVADInstance | null = null
  private _isListening = false

  get isListening(): boolean {
    return this._isListening && !!this.micVAD?.listening
  }

  /**
   * Initialize the VAD with Silero V5 model.
   *
   * @param callbacks - Speech detection event handlers
   * @param config - VAD sensitivity configuration
   * @param sharedStream - Existing MediaStream to share (avoids duplicate mic access)
   */
  async initialize(
    callbacks: VADServiceCallbacks,
    config?: VADServiceConfig,
    sharedStream?: MediaStream
  ): Promise<void> {
    // Dynamic import — ONNX runtime only loaded when open-mic is used
    const { MicVAD } = await import('@ricky0123/vad-web')

    this.micVAD = await MicVAD.new({
      // Model selection: use Silero V5 for best accuracy
      model: 'v5',

      // Explicit asset paths — Vite copies these to dist/vad/ via vite-plugin-static-copy.
      // Without these, the library defaults to "./" which fails in production (nginx
      // SPA fallback returns index.html instead of the binary files).
      baseAssetPath: '/vad/',
      onnxWASMBasePath: '/vad/',

      // Speech detection thresholds
      positiveSpeechThreshold: config?.positiveSpeechThreshold ?? 0.3,
      negativeSpeechThreshold: config?.negativeSpeechThreshold ?? 0.25,
      redemptionMs: config?.redemptionMs ?? 1400,
      minSpeechMs: config?.minSpeechMs ?? 400,
      preSpeechPadMs: config?.preSpeechPadMs ?? 800,

      // Don't auto-start — we control when listening begins
      startOnLoad: false,

      // Share the existing mic stream if provided
      ...(sharedStream
        ? {
            getStream: async () => sharedStream,
            // IMPORTANT: Don't stop shared tracks on pause — AudioCaptureService owns them
            pauseStream: async () => {},
            // Return the same shared stream on resume
            resumeStream: async () => sharedStream,
          }
        : {}),

      // Event callbacks
      onSpeechStart: () => {
        callbacks.onSpeechStart()
      },
      onSpeechEnd: (audio: Float32Array) => {
        callbacks.onSpeechEnd(audio)
      },
      onVADMisfire: () => {
        callbacks.onVADMisfire()
      },
    })
  }

  /** Start listening for speech */
  async start(): Promise<void> {
    if (!this.micVAD) {
      throw new Error('SileroVADService not initialized. Call initialize() first.')
    }
    await this.micVAD.start()
    this._isListening = true
  }

  /** Pause speech detection (mic stays active if shared) */
  async pause(): Promise<void> {
    if (this.micVAD?.listening) {
      await this.micVAD.pause()
    }
    this._isListening = false
  }

  /** Fully destroy the VAD and release resources */
  async destroy(): Promise<void> {
    this._isListening = false
    if (this.micVAD) {
      await this.micVAD.destroy()
      this.micVAD = null
    }
  }
}
